import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ClipboardCheck, Plus, Trash2, Edit2, X, Save, ArrowLeft, Loader2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScorecardCriteria() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ position: '', criteria: [{ key: '', label: '' }] });
  const [saving, setSaving] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/scorecard-criteria`);
      setTemplates(res.data || []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const resetForm = () => {
    setForm({ position: '', criteria: [{ key: '', label: '' }] });
    setEditingId(null);
    setShowForm(false);
  };

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const handleSave = async () => {
    if (!form.position.trim()) { toast.error('Position is required'); return; }
    const validCriteria = form.criteria
      .filter(c => c.label.trim())
      .map(c => ({ key: c.key || slugify(c.label), label: c.label.trim(), description: c.description || '' }));
    if (validCriteria.length === 0) { toast.error('At least one criterion is required'); return; }
    setSaving(true);
    try {
      const payload = { position: form.position.trim(), criteria: validCriteria };
      if (editingId) {
        await axios.put(`${API}/scorecard-criteria/${editingId}`, payload);
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/scorecard-criteria`, payload);
        toast.success('Template created');
      }
      resetForm();
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({
      position: t.position,
      criteria: (t.criteria || []).map(c => ({ ...c })),
    });
    setShowForm(true);
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete the "${t.position}" template?`)) return;
    try {
      await axios.delete(`${API}/scorecard-criteria/${t.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const addCriterion = () => setForm(f => ({ ...f, criteria: [...f.criteria, { key: '', label: '' }] }));
  const removeCriterion = (i) => setForm(f => ({ ...f, criteria: f.criteria.filter((_, idx) => idx !== i) }));
  const updateCriterion = (i, field, value) => setForm(f => ({
    ...f,
    criteria: f.criteria.map((c, idx) => idx === i ? { ...c, [field]: value } : c)
  }));

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">
          <ArrowLeft size={16} className="mr-1" /> Back to Admin
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
            <ClipboardCheck className="text-primary" /> Scorecard Criteria
          </h1>
          <p className="text-muted-foreground mt-1">
            Define interview rating criteria per position (PT, OT, ST, RN, Office Staff, etc.).
            Applicant scorecards automatically use the template matching their position; otherwise the <span className="font-semibold">Default</span> template is used.
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-template-btn">
          <Plus size={16} className="mr-1" /> Add Template
        </Button>
      </div>

      {showForm && (
        <Card className="p-6" data-testid="template-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">{editingId ? 'Edit Template' : 'Add Template'}</h3>
            <Button variant="ghost" size="sm" onClick={resetForm}><X size={16} /></Button>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Position <span className="text-red-500">*</span></Label>
              <Input
                placeholder="e.g. PT, OT, RN, Office Staff"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                data-testid="template-position-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Must match the applicant's position field exactly (case-insensitive). Use <code className="bg-muted px-1 rounded">Default</code> for the fallback template.
              </p>
            </div>

            <div>
              <Label>Criteria</Label>
              <div className="space-y-2 mt-2">
                {form.criteria.map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`criterion-row-${i}`}>
                    <GripVertical size={14} className="text-muted-foreground" />
                    <Input
                      placeholder="Criterion label (e.g. Clinical Skills)"
                      value={c.label}
                      onChange={(e) => updateCriterion(i, 'label', e.target.value)}
                      className="flex-1"
                      data-testid={`criterion-label-${i}`}
                    />
                    <Input
                      placeholder="key (auto)"
                      value={c.key}
                      onChange={(e) => updateCriterion(i, 'key', e.target.value)}
                      className="w-40"
                      data-testid={`criterion-key-${i}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeCriterion(i)} disabled={form.criteria.length === 1}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addCriterion} className="mt-2" data-testid="add-criterion-btn">
                <Plus size={14} className="mr-1" /> Add Criterion
              </Button>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button variant="ghost" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-muted-foreground" size={32} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map(t => (
              <div key={t.id} className="border rounded-lg p-4" data-testid={`template-card-${t.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{t.position}</h3>
                    {t.is_default && t.position === 'Default' && (
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">Fallback</span>
                    )}
                    {t.is_default && t.position !== 'Default' && (
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">Default</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(t)} data-testid={`edit-${t.id}`}>
                      <Edit2 size={14} />
                    </Button>
                    {t.position !== 'Default' && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(t)} data-testid={`delete-${t.id}`}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
                <ul className="text-sm space-y-1">
                  {(t.criteria || []).map(c => (
                    <li key={c.key} className="text-muted-foreground">• {c.label}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
